import React, { useMemo, useRef, useState } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import './ArticleContentEditor.css'
import { ArrowUpTrayIcon, PhotoIcon } from '@heroicons/react/24/outline'

import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'
import { MediaItem, useUploadMedia } from 'hooks/media'
import { unwrapResponse } from 'hooks/article'
import Tooltip from 'components/styledFormElements/Tooltip'
import MediaLibraryModal from './MediaLibraryModal'
import { htmlToPlainText, isRichTextEmpty, newOccurrenceId, resolveMediaUrl } from './helpers'

/* -------------------------------------------------------------------------- */
/*  Custom image blot                                                          */
/*  Quill's stock image format drops unknown attributes. The API needs         */
/*  data-media-id / data-occurrence-id / data-usage-type to survive, so we     */
/*  extend the blot to carry them (and to round-trip them when loading HTML).  */
/* -------------------------------------------------------------------------- */
const BaseImage: any = Quill.import('formats/image')
const Delta: any = Quill.import('delta')
const DATA_ATTRS = ['data-media-id', 'data-occurrence-id', 'data-usage-type']

class MediaImage extends BaseImage {
    static create(value: any) {
        const src = typeof value === 'string' ? value : value?.src
        const node: HTMLElement = super.create(src)
        if (value && typeof value === 'object') {
            ;[...DATA_ATTRS, 'alt'].forEach((attr) => {
                if (value[attr] != null && value[attr] !== '') {
                    node.setAttribute(attr, String(value[attr]))
                }
            })
        }
        return node
    }

    static value(node: HTMLElement) {
        const value: Record<string, string> = { src: node.getAttribute('src') || '' }
        DATA_ATTRS.forEach((attr) => {
            const v = node.getAttribute(attr)
            if (v != null) value[attr] = v
        })
        return value
    }
}
Quill.register(MediaImage, true)

const FORMATS = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'blockquote',
    'link',
    'image',
]

// Images are only allowed via upload / media library (they need a media id).
// Anything pasted without a data-media-id (e.g. base64 screenshots) is dropped.
const MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link'],
        ['clean'],
    ],
    clipboard: {
        matchers: [
            [
                'img',
                (node: HTMLElement, delta: any) =>
                    node.hasAttribute('data-media-id') ? delta : new Delta(),
            ],
        ],
    },
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

const altFromFileName = (name: string) =>
    name
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .trim()

/* -------------------------------------------------------------------------- */

interface ArticleContentEditorProps {
    name: string
    control: Control<any>
    /** Used to build unique occurrence ids; omit while creating. */
    articleId?: number | string | null
    label?: string
    required?: boolean
    error?: string
    disabled?: boolean
}

const ArticleContentEditor = ({
    name,
    control,
    articleId,
    label = 'Content',
    required,
    error,
    disabled,
}: ArticleContentEditorProps) => {
    const notification = useNotificationBar()
    const uploadMedia = useUploadMedia()
    const quillRef = useRef<ReactQuill>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const lastRangeRef = useRef<{ index: number; length: number } | null>(null)
    const [libraryOpen, setLibraryOpen] = useState(false)
    const content = useWatch({ control, name })
    const wordCount = useMemo(() => {
        const text = htmlToPlainText(content)
        return text ? text.split(/\s+/).length : 0
    }, [content])

    const insertMedia = (media: MediaItem) => {
        const editor = quillRef.current?.getEditor()
        if (!editor) return

        const index = lastRangeRef.current?.index ?? Math.max(editor.getLength() - 1, 0)

        editor.insertEmbed(
            index,
            'image',
            {
                src: resolveMediaUrl(media.url || `/media/${media.id}/url`),
                alt: media.alt_text || media.original_name || '',
                'data-media-id': String(media.id),
                // a fresh id per placement; reusing the same image twice needs two ids
                'data-occurrence-id': newOccurrenceId(articleId),
                'data-usage-type': 'content',
            },
            'user'
        )
        editor.setSelection(index + 1, 0, 'silent')
        lastRangeRef.current = { index: index + 1, length: 0 }
    }

    const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            notification.push({
                title: 'Unsupported file',
                type: 'error',
                message: 'Upload a JPG, PNG, GIF or WebP image.',
            })
            return
        }

        try {
            const res = await uploadMedia.mutateAsync({
                file,
                alt_text: altFromFileName(file.name),
            })
            insertMedia(unwrapResponse(res) as MediaItem)
        } catch (err) {
            notification.push(getErrorResponse(err))
        }
    }

    return (
        <div className="article-content-control flex flex-col space-y-2">
            <div className="article-content-heading flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {label}
                    </label>
                    {required && <span className="text-red-700">*</span>}
                    <Tooltip
                        items={[
                            'Write the full article here.',
                            'Insert images with Upload image or Media library.',
                        ]}
                    />
                </div>

                <div className="flex gap-2">
                    <button
                        type="button"
                        disabled={disabled || uploadMedia.isLoading}
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600"
                    >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                        {uploadMedia.isLoading ? 'Uploading…' : 'Upload image'}
                    </button>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => setLibraryOpen(true)}
                        className="flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600"
                    >
                        <PhotoIcon className="h-4 w-4" />
                        Media library
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ALLOWED_IMAGE_TYPES.join(',')}
                        className="hidden"
                        onChange={onFileChosen}
                    />
                </div>
            </div>

            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <div className={`article-editor ${error ? 'has-error' : ''}`}>
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={field.value || ''}
                            onChange={(html) => {
                                const next = isRichTextEmpty(html) ? '' : html
                                if (next !== (field.value || '')) field.onChange(next)
                            }}
                            onChangeSelection={(range) => {
                                if (range) lastRangeRef.current = range
                            }}
                            onBlur={field.onBlur}
                            modules={MODULES}
                            formats={FORMATS}
                            readOnly={disabled}
                            placeholder="Start writing your article…"
                        />
                    </div>
                )}
            />

            <div className="article-content-stats flex items-center justify-between gap-2 text-xs text-slate-400">
                <span>{disabled ? 'Read-only content' : 'Write, format, and add images to tell your story.'}</span>
                <span className="shrink-0 tabular-nums">{wordCount} {wordCount === 1 ? 'word' : 'words'} · {wordCount ? Math.ceil(wordCount / 200) : 0} min read</span>
            </div>
            {error && <span className="text-xs text-red-500">{error}</span>}

            <MediaLibraryModal
                isOpen={libraryOpen}
                onClose={() => setLibraryOpen(false)}
                onSelect={(media) => {
                    setLibraryOpen(false)
                    // wait for the dialog to unmount before restoring editor focus
                    setTimeout(() => insertMedia(media), 0)
                }}
            />
        </div>
    )
}

export default ArticleContentEditor
